import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateAdminProfile } from '@/hooks/useAdminProfile'
import { AdminProfile, AdminProfileForm as AdminProfileFormType } from '@/types'
import { Shield, User, Phone, Mail, Briefcase, AlertTriangle } from 'lucide-react'

const adminProfileSchema = z.object({
  adminName: z.string().min(2, '管理员姓名至少2个字符'),
  adminPhone: z.string().regex(/^1[3-9]\d{9}$/, '手机号格式不正确'),
  adminIdCard: z.string().regex(/^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/, '身份证号格式不正确'),
  adminEmail: z.string().email('邮箱格式不正确').optional().or(z.literal('')),
  adminPosition: z.string().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactPhone: z.string().regex(/^1[3-9]\d{9}$/, '紧急联系人手机号格式不正确').optional().or(z.literal('')),
  remark: z.string().optional(),
})

interface AdminProfileFormProps {
  adminProfile?: AdminProfile
  onSuccess?: () => void
}

export default function AdminProfileForm({ adminProfile, onSuccess }: AdminProfileFormProps) {
  const updateAdminProfileMutation = useUpdateAdminProfile()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<AdminProfileFormType>({
    resolver: zodResolver(adminProfileSchema),
    defaultValues: adminProfile ? {
      adminName: adminProfile.adminName || '',
      adminPhone: adminProfile.adminPhone || '',
      adminIdCard: adminProfile.adminIdCard || '',
      adminEmail: adminProfile.adminEmail || '',
      adminPosition: adminProfile.adminPosition || '',
      emergencyContactName: adminProfile.emergencyContactName || '',
      emergencyContactPhone: adminProfile.emergencyContactPhone || '',
      remark: adminProfile.remark || '',
    } : undefined,
  })

  const onSubmit = async (data: AdminProfileFormType) => {
    try {
      await updateAdminProfileMutation.mutateAsync(data)
      onSuccess?.()
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-5 w-5 text-blue-600" />
          <h3 className="font-medium text-blue-900">管理员配置说明</h3>
        </div>
        <p className="text-sm text-blue-800">
          此配置信息将用于运营商报备和紧急联系。请确保信息真实有效，如有变更请及时更新。
        </p>
      </div>

      {/* 基本信息 */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium flex items-center gap-2">
          <User className="h-5 w-5" />
          基本信息
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="adminName">管理员姓名 *</Label>
            <Input
              id="adminName"
              {...register('adminName')}
              placeholder="请输入管理员姓名"
            />
            {errors.adminName && (
              <p className="text-sm text-red-600">{errors.adminName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="adminPhone">管理员手机号 *</Label>
            <Input
              id="adminPhone"
              {...register('adminPhone')}
              placeholder="请输入管理员手机号"
            />
            {errors.adminPhone && (
              <p className="text-sm text-red-600">{errors.adminPhone.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="adminIdCard">管理员身份证号 *</Label>
            <Input
              id="adminIdCard"
              {...register('adminIdCard')}
              placeholder="请输入管理员身份证号"
            />
            {errors.adminIdCard && (
              <p className="text-sm text-red-600">{errors.adminIdCard.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="adminEmail">管理员邮箱</Label>
            <Input
              id="adminEmail"
              type="email"
              {...register('adminEmail')}
              placeholder="请输入管理员邮箱"
            />
            {errors.adminEmail && (
              <p className="text-sm text-red-600">{errors.adminEmail.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="adminPosition">管理员职位</Label>
            <Input
              id="adminPosition"
              {...register('adminPosition')}
              placeholder="如：技术总监、运营经理等"
            />
          </div>
        </div>
      </div>

      {/* 紧急联系人 */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          紧急联系人
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="emergencyContactName">紧急联系人姓名</Label>
            <Input
              id="emergencyContactName"
              {...register('emergencyContactName')}
              placeholder="请输入紧急联系人姓名"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="emergencyContactPhone">紧急联系人手机号</Label>
            <Input
              id="emergencyContactPhone"
              {...register('emergencyContactPhone')}
              placeholder="请输入紧急联系人手机号"
            />
            {errors.emergencyContactPhone && (
              <p className="text-sm text-red-600">{errors.emergencyContactPhone.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* 备注信息 */}
      <div className="space-y-2">
        <Label htmlFor="remark">备注信息</Label>
        <Textarea
          id="remark"
          {...register('remark')}
          placeholder="其他需要说明的信息"
          rows={3}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? '保存中...' : '保存配置'}
        </Button>
      </div>
    </form>
  )
}
